// Frictionless top-of-funnel email capture for resource/guide pages (DF-85).
//
// No account required, no Firestore write: every Firestore collection needs
// isSignedIn() (firestore.rules) and a new public-write collection needs a
// rules deploy, which is human-gated (DF-92). Same constraint MicroSurvey.tsx
// solved for DF-104 — capture via posthog.capture() (queryable immediately,
// no deploy dependency) and export the list for human outreach once DF-92/
// DF-106 land, rather than blocking capture on infra that isn't ready yet.

import { useState } from 'react';
import { posthog } from '@/lib/analytics/posthog';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailCaptureProps {
  page: string;
}

function ArticleFeedback({ page }: EmailCaptureProps) {
  const [answered, setAnswered] = useState<'idle' | 'helpful' | 'not_helpful'>('idle');

  function vote(helpful: boolean) {
    try {
      posthog.capture('resource_feedback_given', { page, helpful });
    } catch {
      /* posthog not initialised — nothing to record */
    }
    setAnswered(helpful ? 'helpful' : 'not_helpful');
  }

  return (
    <div className="mb-4 flex items-center gap-3 text-sm text-slate-600">
      {answered === 'idle' ? (
        <>
          <span>Was this article helpful?</span>
          <button
            type="button"
            onClick={() => vote(true)}
            aria-label="Yes, this article was helpful"
            className="rounded-md border border-slate-300 px-2 py-1 hover:border-primary hover:text-primary"
          >
            👍 Yes
          </button>
          <button
            type="button"
            onClick={() => vote(false)}
            aria-label="No, this article was not helpful"
            className="rounded-md border border-slate-300 px-2 py-1 hover:border-primary hover:text-primary"
          >
            👎 No
          </button>
        </>
      ) : (
        <span>Thanks for letting us know 🙏</span>
      )}
    </div>
  );
}

export default function EmailCapture({ page }: EmailCaptureProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitted' | 'error'>('idle');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setStatus('error');
      return;
    }
    try {
      posthog.capture('email_capture_lead', {
        email: email.trim(),
        page,
        $set: { email: email.trim() },
      });
    } catch {
      /* posthog not initialised — nothing to record */
    }
    setStatus('submitted');
  }

  if (status === 'submitted') {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center">
        <p className="text-sm font-medium text-slate-800">
          Thanks — we&apos;ll send new guides like this one to your inbox. 🙏
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
      <ArticleFeedback page={page} />
      <p className="mb-1 text-sm font-semibold text-slate-800">
        Want more guides like this?
      </p>
      <p className="mb-3 text-sm text-slate-600">
        Occasional emails on giving, taxes, and fundraising for missionaries — no spam, unsubscribe anytime.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === 'error') setStatus('idle');
          }}
          placeholder="you@example.com"
          aria-label="Email address"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-custom-blue focus:outline-none"
        />
        <button
          type="submit"
          className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
        >
          Send me guides
        </button>
      </form>
      {status === 'error' && (
        <p className="mt-2 text-xs text-red-600">Enter a valid email address.</p>
      )}
    </div>
  );
}
